import React from 'react'
import PropTypes from 'prop-types'
import Button from 'react-bootstrap/Button'
import history from '../../../history'
import { trackInternalLinkClick, trackExternalLinkClick } from '../../../utilities/analyticsEventHelpers'
import {
  extractRelativePath,
  isEmailAddress, handleExternalNavigation, isExternalLink
} from '../../../utilities/linkHelpers'

const LinkAnchor = props => {
  const {
    children,
    className,
    href,
    linkProps,
    title,
    ariaLabel
  } = props

  return (
    <a
      className={className}
      href={href}
      title={title}
      {...linkProps}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  )
}

LinkAnchor.propTypes = {
  /* see LinkHandler proper for real children validation */
  children: PropTypes.any,
  className: PropTypes.string,
  href: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  /* internal/external link handling props */
  linkProps: PropTypes.object,
  title: PropTypes.string
}

const LinkButton = props => {
  const {
    children,
    className,
    href,
    linkProps,
    title,
    ariaLabel,
    variant
  } = props

  return (
    <Button
      className={className}
      href={href}
      title={title}
      aria-label={ariaLabel}
      variant={variant}
      {...linkProps}
    >
      {children}
    </Button>
  )
}

LinkButton.propTypes = {
  /* see LinkHandler proper for real children validation */
  children: PropTypes.any,
  className: PropTypes.string,
  href: PropTypes.string.isRequired,
  /* internal/external link handling props */
  linkProps: PropTypes.object,
  ariaLabel: PropTypes.string,
  title: PropTypes.string,
  variant: PropTypes.string
}

function LinkHandler (props) {
  const {
    children,
    href,
    label,
    noBs = false,
    routerData = {},
    event,
    clickCallBack,
    analyticsOverride
  } = props

  const hrefIsExternal = isExternalLink(href)
  const hrefIsEmail = isEmailAddress(href)

  const handleInternalLinkClick = (e) => {
    const { location: { pathname: currentPathname } } = history

    e.preventDefault()

    clickCallBack && clickCallBack()

    const targetHref = e.currentTarget.getAttribute('href')
    const pathname = extractRelativePath(targetHref)
    /* may need feedback on alternative to pass in the case of innerHTML/icon */
    const targetElement = e.currentTarget.innerText !== '' ? e.currentTarget.innerText : e.currentTarget.innerHTML
    trackInternalLinkClick(event, pathname, targetElement, window.utag)
    // addresses situations where user clicks on link that is the same as the current location;
    // in these cases we do not want to continue to push duplicate entries onto the history stack;
    // this situation is most likely to happen in the global header menu or footer
    const historyMethod = pathname === currentPathname ? 'replace' : 'push'

    history[historyMethod]({
      pathname: pathname,
      ...routerData
    })
  }

  // link handling centers around internal/external prop sets (email links are treated as external links)
  const externalProps = {
    target: '_blank',
    ...(!hrefIsEmail ? { rel: 'noopener noreferrer' } : {}),
    onClick: (e) => {
      const url = e.currentTarget.href
      // keep analytics
      analyticsOverride
        ? analyticsOverride(e)
        : trackExternalLinkClick(e, window.utag)

      // checks whether to open confirmation modal
      handleExternalNavigation(url, e)
    }
  }

  const internalProps = {
    onClick: analyticsOverride || handleInternalLinkClick
  }

  const linkProps = hrefIsExternal ? externalProps : internalProps

  // Exclude props that shouldn't be passed to DOM elements
  const { noBs: _, routerData: __, event: ___, clickCallBack: ____, analyticsOverride: _____, ...restProps } = props

  const allProps = {
    ...restProps,
    title: props.title || '',
    variant: props.variant || 'link',
    linkProps
  }

  const LinkComponent = noBs ? LinkAnchor : LinkButton

  return (
    <LinkComponent {...allProps}>
      {label}
      {children}
    </LinkComponent>
  )
}

LinkHandler.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  href: PropTypes.string.isRequired,
  label: PropTypes.string,
  /* opt out of any bootstrap styling for a plain <a> */
  noBs: PropTypes.bool,
  /* optional data to pass along to internal routes */
  routerData: PropTypes.object,
  /* native tooltip */
  title: PropTypes.string,
  /* bootstrap styling pass through */
  variant: PropTypes.string,
  /* optional analytics event data */
  event: PropTypes.object,
  /* optional code to run on click event */
  clickCallBack: PropTypes.func,
  /* Optional callback function to handle analytics outside of the standard internal/external tracking */
  analyticsOverride: PropTypes.func
}

export default LinkHandler
